import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import {EvaluationStatusBadge} from '~/components/admin/pages/edit/EditSoftwareQualityIndicator'
import {SoftwareEvaluationResult} from '~/components/software/indicator/apiSoftwareIndicator'
import {DynamicTable, DynamicTableColumn, summarizeList} from '~/components/software/indicator/DynamicTable'
import {SoftwareEvaluationDataForReporting} from './apiOrganisationSoftwareQuality'
import {useSoftwareQualityOverviewContext} from './SoftwareQualityOverviewContext'
import {QualityReportDownloader} from './QualityReportDownloader'
import WarningIcon from '@mui/icons-material/Warning'
import Tooltip from '@mui/material/Tooltip'


export type OrganisationReportingTableData = {
  id: string
  year: string
  software_name: string
  software_slug: string
  pof_topic_short_name: string | null | undefined
  contact_person_name: string | null | undefined
  artefact: string | null | undefined
  participating_organisations: string[]
  submitted: boolean | undefined
  last_updated: string | undefined
  evaluation_result: SoftwareEvaluationResult
}

export function OrganisationSoftwareQualityOverview() {
  const ctx = useSoftwareQualityOverviewContext()

  function mapToReportingTable(data: SoftwareEvaluationDataForReporting[]): OrganisationReportingTableData[] {
    const tableData: OrganisationReportingTableData[] = data.map(evaluation => {return {
      id: evaluation.id,
      year: evaluation.year,
      software_name: evaluation.software.name,
      software_slug: evaluation.software.slug,
      pof_topic_short_name: evaluation.centre_reporting_information.pof_topic.short_name,
      contact_person_name: evaluation.centre_reporting_information.contact_person.name,
      artefact: evaluation.current_artefact?.artefact,
      participating_organisations: evaluation.participating_centres.map(centre => centre.toUpperCase()),
      submitted: evaluation.current_evaluation_status.submitted,
      last_updated: evaluation.current_evaluation_status.last_updated,
      evaluation_result: evaluation.current_evaluation_status.evaluation_result
    }})
    return tableData
  }

  const OrganisationReportingOverviewColumns: DynamicTableColumn<OrganisationReportingTableData>[] = [
    {
      label: 'Software',
      field: 'software_name',
      render: (row) => <Button variant='text' href={`/software/${row.software_slug}`} target='_blank'>{row.software_name}</Button>
    },
    {
      label: 'PoF Topic',
      field: 'pof_topic_short_name',
      render: (row) => row.pof_topic_short_name ?? <Tooltip title='No information provided.' placement="top" arrow><div className='w-full text-center'><WarningIcon color='warning'/></div></Tooltip>,
    },
    {
      label: 'Artefact',
      field: 'artefact',
      render: (row) => row.artefact ?? <Tooltip title='No information provided.' placement="top" arrow><div className='w-full text-center'><WarningIcon color='warning'/></div></Tooltip>,
    },
    {
      label: 'Contact Person',
      field: 'contact_person_name',
      render: (row) => row.contact_person_name ?? <Tooltip title='No information provided.' placement="top" arrow><div className='w-full text-center'><WarningIcon color='warning'/></div></Tooltip>,
    },
    {
      label: 'Participating Organisations',
      field: 'participating_organisations',
      render: (row) => summarizeList(row.participating_organisations, null)
    },
    {
      label: 'Submit Status',
      field: 'submitted',
      filterable: true,
      render: (row) => {
        const parsedDate = new Date(row.last_updated ?? '')
        return row.submitted ? <Stack>Submitted <p className='text-xs'>{parsedDate.toLocaleString()}</p></Stack>
          : 'Not submitted'
      },
      filterRender: (value) => value ? 'Submitted' : 'Not submitted',
      defaultFilterValues: [true]
    },
    {
      label: 'Evaluation Result',
      field: 'evaluation_result',
      filterable: true,
      render: (row) => <EvaluationStatusBadge evaluationStatus={row.evaluation_result}/>,
      filterRender: (value) => value,
      defaultFilterValues: ['counted']
    }
  ]

  const countedPercentageOfSubmissions = () => {
    const evaluationsSubmitted = ctx.evaluatedSoftware.filter(entry => entry.current_evaluation_status.submitted === true)
    const submissionsCounted = evaluationsSubmitted.filter(entry => entry.current_evaluation_status.evaluation_result === 'counted').length
    return (evaluationsSubmitted.length ? (submissionsCounted / evaluationsSubmitted.length)*100 : 0).toFixed(1)
  }

  const countedUnsubmittedEvaluations = () => {
    return ctx.evaluatedSoftware.filter(entry => entry.current_evaluation_status.submitted === false).length
  }

  const lastDataDownloadTimestamp = ctx.savedQualityReports[0] ? ctx.savedQualityReports[0].timestamp : undefined
  const lastDataChangeTimestamp = ctx.evaluatedSoftware
    .map((software) => software.current_evaluation_status.last_updated)
    .sort()
    .at(-1)
  let newerSubmissionExist
  if (lastDataDownloadTimestamp && lastDataChangeTimestamp) {
    newerSubmissionExist = lastDataChangeTimestamp > lastDataDownloadTimestamp
  } else if (lastDataDownloadTimestamp) {
    newerSubmissionExist = false
  } else {
    newerSubmissionExist = true
  }

  return (
    <>
      <Stack direction='row' className='gap-4 pb-8'>
        <SoftwareQualityReportingMetricCard
          title='Total submitted software'
          metric={ctx.evaluatedSoftware.filter(entry => entry.current_evaluation_status.submitted === true).length}
          info={`Unsubmitted: ${countedUnsubmittedEvaluations()}`}
        />
        <SoftwareQualityReportingMetricCard
          title='Meeting minimum requirements'
          metric={ctx.evaluatedSoftware.filter(entry => entry.current_evaluation_status.submitted === true && entry.current_evaluation_status.evaluation_result === 'counted').length}
          info={`${countedPercentageOfSubmissions()}% of all submissions`}
        />
        <SoftwareQualityReportingMetricCard
          title='Last data download'
          metric={lastDataDownloadTimestamp ? (new Date(lastDataDownloadTimestamp)).toLocaleString() : 'never'}
          info={newerSubmissionExist ? 'Note: There are newer submissions.' : 'No new submissions since then.'}
        />
        <QualityReportDownloader />
      </Stack>
      <DynamicTable columns={OrganisationReportingOverviewColumns} data={mapToReportingTable(ctx.evaluatedSoftware) ?? []} getRowId={(row) => row.id} />
    </>
  )
}

export function SoftwareQualityReportingMetricCard({title, metric, info}:{title: string, metric: string | number | boolean, info?: string}) {
  return (
    <Card variant="outlined" className='w-1/4'>
      <Stack className='justify-between p-4 h-full'>
        <Stack>
          <Typography gutterBottom sx={{color: 'text.secondary', fontSize: 14}}>
            {title}
          </Typography>
          <Typography variant="h4" component="div">
            {metric}
          </Typography>
        </Stack>
        <Typography gutterBottom sx={{color: 'text.secondary', fontSize: 14}}>
          {info}
        </Typography>
      </Stack>
    </Card>
  )
}
